/**
 * Shared property strategy allocator UI for Support at Home.
 * Used by StrategiesSection and ServiceBuilderSection.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { HelpCircle, Home, Gift, ShieldCheck, Wallet, Info, ChevronDown, X, Plus, TrendingUp, TrendingDown } from 'lucide-react';
import { calculateAge } from '../../utils/index.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { PENSION_RATES, DOWNSIZER_CONTRIBUTION_CAP, CurrencyInput, calculateCgtForStrategy, SUPPORT_ASSET_OPTIONS } from './SupportAtHomeHelpers.tsx';
import type { RenovateDetails, UpgradeDetails, PropertyStrategyFundingSource, FinancialsForCgt } from './SupportAtHomeHelpers.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
import { strategyExplainers } from '../../strategy/strategyExplainers.ts';
import { notify } from '../../components/ui/notices.tsx';
import { assetDisplayName } from '../../utils/assetDisplayName.ts';
// Simple InfoIcon Component
const InfoIcon = ({ text }: { text: string }) => {
    const [show, setShow] = useState(false);
    return (
        <span className="relative inline-block ml-1 align-middle" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
            <HelpCircle size={16} className="text-gray-500 cursor-pointer" />
            {show && (
                <div className="absolute left-0 bottom-full mb-2 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg z-50 whitespace-pre-line" style={{ width: '320px' }}>
                    {text}
                </div>
            )}
        </span>
    );
};

interface SupportAllocationInputProps {
    icon?: React.ComponentType<{ size?: number; className?: string }>;
    label: string;
    field: string;
    value: number | string;
    max: number;
    onChange: (field: string, value: string | number | undefined) => void;
    tooltip?: string;
    disabled?: boolean;
}

// SupportAllocationInput helper component
/**
 * The allocator's own unallocated-proceeds arithmetic, exported so the recap quotes the SAME
 * figure this panel displays (the frame rule: one computation, quoted twice).
 */
export function computeSahDownsizeUnallocated(
    factFind: DraftFactFind,
    details: SupportDownsizeAllocatorDetails | undefined,
): number {
    const homeValue = parseFloat(String(factFind.home?.value)) || 0;
    const salePrice = parseFloat(String(details?.salePrice ?? homeValue)) || homeValue;
    const sellingCosts = parseFloat(String(details?.sellingCosts)) || 0;
    const netProceeds = Math.max(0, salePrice - sellingCosts);
    const baseKeys: (keyof SupportDownsizeAllocatorDetails)[] = ['newHomePurchasePrice', 'downsizerContribution', 'partnerDownsizerContribution', 'giftingAmount', 'debtRepayment'];
    const baseTotal = baseKeys.reduce<number>((sum, key) => sum + (parseFloat(String(details?.[key])) || 0), 0);
    const assetTotal = (details?.addedAssets || []).reduce((sum, asset) => sum + (parseFloat(String(asset.value)) || 0), 0);
    return Math.max(0, netProceeds - (baseTotal + assetTotal));
}

const SupportAllocationInput = ({ icon: Icon, label, field, value, max, onChange, tooltip, disabled }: SupportAllocationInputProps) => (
    <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
            {Icon && <Icon size={16} className="text-gray-600" />}
            <span>{label}</span>
            {tooltip && <InfoIcon text={tooltip} />}
        </label>
        <div className="flex items-center gap-3">
            <input
                type="range"
                min="0"
                max={Math.max(0, Math.floor(Number(max) || 0))}
                step="1000"
                value={value || 0}
                onChange={(e) => onChange(field, e.target.value)}
                className="flex-1 h-2 bg-gray-200 rounded-full appearance-none accent-blue-600 disabled:opacity-50"
                disabled={disabled}
            />
            <div className="w-32">
                <CurrencyInput
                    value={value === '' ? '' : value}
                    onChange={(v) => onChange(field, v)}
                    placeholder="0"
                    disabled={disabled}
                    className=""
                />
            </div>
        </div>
    </div>
);

// Property Strategy Allocator Components

/** A financial or personal asset added to fund a downsize strategy's surplus proceeds. */
export interface SupportDownsizeAddedAsset {
    id: string;
    type: string;
    description: string;
    value: number | string;
    ownership: string;
    incomeRate?: number | string;
    growthRate?: number | string;
    frankingCredit?: number | string;
}

/** Editable state for SupportDownsizeStrategyAllocator — a superset of supportAtHomeStrategyFunding.ts's
 * DownsizeDetails, widened to `number | string` while the user edits via CurrencyInput/slider controls. */
export interface SupportDownsizeAllocatorDetails {
    salePrice?: number | string;
    sellingCosts?: number | string;
    newHomePurchasePrice?: number | string;
    downsizerContribution?: number | string;
    partnerDownsizerContribution?: number | string;
    giftingAmount?: number | string;
    debtRepayment?: number | string;
    selectedDebtKey?: string;
    addedAssets?: SupportDownsizeAddedAsset[];
    commenceIncomeStream?: boolean;
    showDownsizerInfo?: boolean;
    [key: string]: unknown;
}

export interface SupportDownsizeStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: SupportDownsizeAllocatorDetails;
    setDetails: React.Dispatch<React.SetStateAction<SupportDownsizeAllocatorDetails>>;
}

interface LiabilityOption {
    key: string;
    label: string;
    balance: number;
}

export function SupportDownsizeStrategyAllocator({ factFind, details, setDetails }: SupportDownsizeStrategyAllocatorProps) {
    const [showAssetForm, setShowAssetForm] = useState(false);
    const [newAsset, setNewAsset] = useState(() => ({
        type: '',
        description: '',
        value: '' as number | string,
        incomeRate: '' as number | string,
        growthRate: '' as number | string,
        ownership: factFind?.adviceFor === 'couple' ? 'joint' : 'client',
        frankingCredit: '' as number | string,
    }));

    const formatCurrencyLocal = (value: number | string | undefined) =>
        new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value) || 0);

    const homeValue = parseFloat(String(factFind.home?.value)) || 0;
    const salePrice = parseFloat(String(details.salePrice ?? homeValue)) || homeValue;
    const sellingCosts = parseFloat(String(details.sellingCosts)) || 0;
    const netProceeds = Math.max(0, salePrice - sellingCosts);

    const addedAssets = Array.isArray(details.addedAssets) ? details.addedAssets : [];

    const sumAllocations = (input: SupportDownsizeAllocatorDetails | undefined): number => {
        const baseKeys: (keyof SupportDownsizeAllocatorDetails)[] = ['newHomePurchasePrice', 'downsizerContribution', 'partnerDownsizerContribution', 'giftingAmount', 'debtRepayment'];
        const baseTotal = baseKeys.reduce<number>((sum, key) => sum + (parseFloat(String(input?.[key])) || 0), 0);
        const assetTotal = (input?.addedAssets || []).reduce((sum, asset) => sum + (parseFloat(String(asset.value)) || 0), 0);
        return baseTotal + assetTotal;
    };

    const totalAllocated = sumAllocations(details);
    const unallocatedProceeds = netProceeds - totalAllocated;

    const liabilityOptions = useMemo<LiabilityOption[]>(() => {
        if (!factFind?.liabilities) return [];
        const options: LiabilityOption[] = [];
        Object.entries(factFind.liabilities).forEach(([type, items]) => {
            if (!Array.isArray(items)) return;
            items.forEach(item => {
                const balance = Math.max(0, item.balance || item.value || 0);
                if (balance > 0) {
                    options.push({
                        key: `${type}:${item.id}`,
                        label: `${item.description || 'Debt'} (${formatCurrencyLocal(balance)})`,
                        balance
                    });
                }
            });
        });
        return options;
    }, [factFind?.liabilities]);

    useEffect(() => {
        if (liabilityOptions.length > 0 && !details.selectedDebtKey) {
            setDetails(prev => ({ ...prev, selectedDebtKey: liabilityOptions[0].key }));
        }
    }, [liabilityOptions, details.selectedDebtKey, setDetails]);

    const selectedDebt = liabilityOptions.find(option => option.key === details.selectedDebtKey) || null;

    const handleAllocationChange = (field: string, rawValue: number | string | undefined) => {
        const numeric = Math.max(0, parseFloat(String(rawValue)) || 0);
        const detailsWithoutField = { ...details, [field]: 0 };
        const otherAllocated = sumAllocations(detailsWithoutField);
        const available = Math.max(0, netProceeds - otherAllocated);

        let capped = Math.min(numeric, available);
        if (field === 'downsizerContribution' || field === 'partnerDownsizerContribution') {
            capped = Math.min(capped, DOWNSIZER_CONTRIBUTION_CAP);
        }
        if (field === 'debtRepayment' && selectedDebt) {
            capped = Math.min(capped, selectedDebt.balance);
        }

        setDetails(prev => ({
            ...prev,
            [field]: Number.isNaN(capped) ? 0 : capped
        }));
    };

    const handleBasicChange = (field: string, value: number | undefined) => {
        setDetails(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const resetAssetForm = () => {
        setNewAsset({
            type: '',
            description: '',
            value: '',
            incomeRate: '',
            growthRate: '',
            ownership: factFind?.adviceFor === 'couple' ? 'joint' : 'client',
            frankingCredit: ''
        });
    };

    const nonAssetAllocation = (['newHomePurchasePrice', 'downsizerContribution', 'partnerDownsizerContribution', 'giftingAmount', 'debtRepayment'] as const)
        .reduce((sum, key) => sum + (parseFloat(String(details[key])) || 0), 0);
    const existingAssetTotal = addedAssets.reduce((sum, asset) => sum + (parseFloat(String(asset.value)) || 0), 0);
    const availableForNewAsset = Math.max(0, netProceeds - nonAssetAllocation - existingAssetTotal);

    const handleAddAsset = () => {
        if (!newAsset.type) {
            notify('Please select an asset type.');
            return;
        }
        const assetValue = Math.max(0, parseFloat(String(newAsset.value)) || 0);
        if (assetValue <= 0) {
            notify('Please enter a positive amount for the asset.');
            return;
        }
        if (assetValue > availableForNewAsset) {
            notify('The amount exceeds the available proceeds.');
            return;
        }

        const config = SUPPORT_ASSET_OPTIONS.find(opt => opt.id === newAsset.type);
        const defaults = config?.defaults || { incomeRate: '', growthRate: '', frankingCredit: '' };
        const assetId = `downsize-asset-${Date.now()}`;

        setDetails(prev => ({
            ...prev,
            addedAssets: [
                ...(prev.addedAssets || []),
                {
                    id: assetId,
                    type: newAsset.type,
                    description: newAsset.description || config?.label || 'Allocated investment',
                    value: assetValue,
                    ownership: newAsset.ownership || (factFind?.adviceFor === 'couple' ? 'joint' : 'client'),
                    incomeRate: newAsset.incomeRate || defaults.incomeRate || '',
                    growthRate: newAsset.growthRate || defaults.growthRate || '',
                    frankingCredit: newAsset.frankingCredit || defaults.frankingCredit || ''
                }
            ]
        }));
        resetAssetForm();
        setShowAssetForm(false);
    };

    const removeAsset = (id: string) => {
        setDetails(prev => ({
            ...prev,
            addedAssets: (prev.addedAssets || []).filter(asset => asset.id !== id)
        }));
    };

    const ownershipOptions = factFind?.adviceFor === 'couple'
        ? [
            { value: 'joint', label: 'Joint' },
            { value: 'client', label: factFind?.client?.firstName || 'Client' },
            { value: 'partner', label: factFind?.partner?.firstName || 'Partner' }
        ]
        : [{ value: 'client', label: factFind?.client?.firstName || 'Client' }];

    const clientAge = calculateAge(factFind.client?.dob);
    const isDownsizerEligible = (clientAge || 0) >= 55;

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-6">
            <div>
                <h5 className="font-semibold text-gray-900 text-base mb-2">Transaction assumptions</h5>
                <div className="grid gap-4 md:grid-cols-3">
                    <div>
                        <CurrencyInput
                            label="Sale price"
                            value={details.salePrice === '' ? '' : (details.salePrice ?? '')}
                            onChange={(v) => handleBasicChange('salePrice', v)}
                            className=""
                        />
                        <p className="text-xs text-gray-500 mt-1">Current home value is {formatCurrencyLocal(homeValue)}.</p>
                    </div>
                    <div>
                        <CurrencyInput
                            label="Selling costs"
                            value={details.sellingCosts === '' ? '' : (details.sellingCosts ?? '')}
                            onChange={(v) => handleBasicChange('sellingCosts', v)}
                            className=""
                        />
                        <p className="text-xs text-gray-500 mt-1">Agent fees, legal costs, staging, etc.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Net proceeds</label>
                        <div className="px-3 py-2 border border-blue-200 bg-white rounded-lg font-semibold text-blue-700">
                            {formatCurrencyLocal(netProceeds)}
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <h5 className="font-semibold text-gray-900 text-base mb-2">Allocate the proceeds</h5>
                <div className="mt-2 p-3 bg-white border-l-4 border-blue-400 rounded-r-lg text-sm flex justify-between items-center">
                    <p><strong className="text-blue-700">Unallocated Proceeds:</strong></p>
                    <p className={`font-bold text-lg ${unallocatedProceeds < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrencyLocal(unallocatedProceeds)}
                    </p>
                </div>
                {/* The LIVE why, standing beside the number that causes it (owner-caught,
                    2026-08-25): the pension snapped to nil the moment the sale left money
                    unallocated, and nothing on the page said why. Transposed from the insight
                    panel's what-happens-to-the-money narrative; the deep layer is behind the tap. */}
                {unallocatedProceeds > 0 && (
                    <p className="mt-2 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700 shadow-sm">
                        Until it's reallocated, this {formatCurrencyLocal(unallocatedProceeds)} sits in the bank —
                        where the home was exempt, bank money is <span className="font-semibold">counted in the assets
                        test and deemed to earn income</span>. That is what moves the Age Pension and the contribution
                        rates on the right{' '}
                        <JargonTerm term="(the full chain)">
                            {`The home is exempt from the means tests; money in the bank is not. The sale converts exempt value into counted value: the unallocated ${formatCurrencyLocal(unallocatedProceeds)} is added to assessable assets, and deeming assumes it earns income for the income test. If that pushes past the pension cut-off, the Age Pension stops entirely — and with no pension, the household is assessed as fully self-funded, which carries the highest Support at Home contribution rates. Allocating the money below — a new home, downsizer super contributions, gifts within limits — moves it back out of the counted pile.`}
                        </JargonTerm>
                    </p>
                )}

                <div className="mt-4 space-y-4">
                    <SupportAllocationInput
                        icon={Home}
                        label="Purchase new home"
                        field="newHomePurchasePrice"
                        value={details.newHomePurchasePrice || 0}
                        max={netProceeds}
                        onChange={handleAllocationChange}
                    />
                    <SupportAllocationInput
                        icon={Wallet}
                        label="Downsizer contribution"
                        field="downsizerContribution"
                        value={details.downsizerContribution || 0}
                        max={Math.min(DOWNSIZER_CONTRIBUTION_CAP, netProceeds)}
                        onChange={handleAllocationChange}
                        tooltip={strategyExplainers.downsizerToSuper(isDownsizerEligible)}
                        disabled={!isDownsizerEligible}
                    />
                    <div className="mt-1">
                        <button
                            type="button"
                            onClick={() => setDetails(prev => ({ ...prev, showDownsizerInfo: !prev.showDownsizerInfo }))}
                            className="text-xs text-blue-600 underline"
                        >
                            {details.showDownsizerInfo ? 'Hide downsizer explainer' : 'Learn about downsizer contribution'}
                        </button>
                        {details.showDownsizerInfo && (
                            <div className="mt-3 space-y-2 text-xs bg-white border border-blue-200 rounded-lg p-3 text-blue-900">
                                <div>
                                    <p className="font-semibold text-blue-800">What is it?</p>
                                    <p>Allows eligible homeowners aged 55+ to contribute up to {formatCurrencyLocal(DOWNSIZER_CONTRIBUTION_CAP)} (each) from sale proceeds into super without impacting contribution caps.</p>
                                </div>
                                <div>
                                    <p className="font-semibold text-blue-800">Benefits</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Moves funds into the tax-effective super environment.</li>
                                        <li>No contribution cap or work test restrictions.</li>
                                        <li>Can commence a new income stream once in pension phase.</li>
                                    </ul>
                                </div>
                                <div>
                                    <p className="font-semibold text-blue-800">Eligibility</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Age 55+ at the time of contribution.</li>
                                        <li>Home owned for 10+ years and eligible for CGT main residence exemption.</li>
                                        <li>Contribution generally made within 90 days of settlement.</li>
                                    </ul>
                                </div>
                                <div>
                                    <p className="font-semibold text-blue-800">Considerations/Risks</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Counts as an assessable asset for the Age Pension.</li>
                                        <li>Contribution is irreversible — retain sufficient cash reserves.</li>
                                        <li>Seek personal financial advice before proceeding.</li>
                                    </ul>
                                </div>
                                <div>
                                    <a
                                        href="https://www.ato.gov.au/individuals/super/growing-your-super/adding-to-your-super/downsizer-contributions-for-individuals/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-700 underline"
                                    >
                                        More information from the ATO
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                    {factFind?.adviceFor === 'couple' && (
                        <SupportAllocationInput
                            icon={Wallet}
                            label="Partner downsizer contribution"
                            field="partnerDownsizerContribution"
                            value={details.partnerDownsizerContribution || 0}
                            max={Math.min(DOWNSIZER_CONTRIBUTION_CAP, netProceeds)}
                            onChange={handleAllocationChange}
                            tooltip={strategyExplainers.partnerDownsizerToSuper(isDownsizerEligible)}
                            disabled={!isDownsizerEligible}
                        />
                    )}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="commenceIncomeStream"
                            checked={!!details.commenceIncomeStream}
                            onChange={(e) => setDetails(prev => ({ ...prev, commenceIncomeStream: e.target.checked }))}
                        />
                        <label htmlFor="commenceIncomeStream" className="text-sm text-gray-700">Commence an income stream from downsizer contribution</label>
                    </div>
                    <SupportAllocationInput
                        icon={Gift}
                        label="Gift to loved ones"
                        field="giftingAmount"
                        value={details.giftingAmount || 0}
                        max={netProceeds}
                        onChange={handleAllocationChange}
                        tooltip={strategyExplainers.giftToLovedOnes()}
                    />
                    {liabilityOptions.length > 0 && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Repay debt</label>
                            <select
                                value={details.selectedDebtKey || ''}
                                onChange={(e) => setDetails(prev => ({ ...prev, selectedDebtKey: e.target.value, debtRepayment: 0 }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            >
                                {liabilityOptions.map(option => (
                                    <option key={option.key} value={option.key}>{option.label}</option>
                                ))}
                            </select>
                            <SupportAllocationInput
                                icon={ShieldCheck}
                                label="Amount to repay"
                                field="debtRepayment"
                                value={details.debtRepayment || 0}
                                max={selectedDebt ? Math.min(selectedDebt.balance, netProceeds) : 0}
                                onChange={handleAllocationChange}
                                tooltip={strategyExplainers.payDownLoan()}
                            />
                        </div>
                    )}

                    <div className="border border-dashed border-blue-300 rounded-lg p-4 bg-white space-y-3">
                        <div className="flex items-center justify-between">
                            <h6 className="font-semibold text-gray-900 text-sm">Add financial & personal assets</h6>
                            <button
                                type="button"
                                onClick={() => setShowAssetForm(prev => !prev)}
                                className="text-sm text-blue-600 underline"
                            >
                                {showAssetForm ? 'Cancel' : 'Add asset'}
                            </button>
                        </div>
                        {showAssetForm && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Asset type</label>
                                    <select
                                        value={newAsset.type}
                                        onChange={(e) => {
                                            const type = e.target.value;
                                            const defaults = SUPPORT_ASSET_OPTIONS.find(opt => opt.id === type)?.defaults || { incomeRate: 0, growthRate: 0, frankingCredit: 0 };
                                            setNewAsset(prev => ({
                                                ...prev,
                                                type,
                                                incomeRate: defaults.incomeRate || prev.incomeRate,
                                                growthRate: defaults.growthRate || prev.growthRate,
                                                frankingCredit: defaults.frankingCredit || prev.frankingCredit
                                            }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="">Select asset type...</option>
                                        {SUPPORT_ASSET_OPTIONS.map(option => (
                                            <option key={option.id} value={option.id}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                                    <input
                                        type="text"
                                        value={newAsset.description}
                                        onChange={(e) => setNewAsset(prev => ({ ...prev, description: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        placeholder="e.g. Australian shares portfolio"
                                    />
                                </div>
                                <div>
                                    <CurrencyInput
                                        label="Amount allocated"
                                        value={newAsset.value === '' ? '' : newAsset.value}
                                        onChange={(v) => setNewAsset(prev => ({ ...prev, value: v ?? '' }))}
                                        className="text-sm"
                                        placeholder="0"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">Available: {formatCurrencyLocal(availableForNewAsset)}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Ownership</label>
                                    <select
                                        value={newAsset.ownership}
                                        onChange={(e) => setNewAsset(prev => ({ ...prev, ownership: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {ownershipOptions.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Income rate (% p.a.)</label>
                                    <input
                                        type="number"
                                        value={newAsset.incomeRate}
                                        onChange={(e) => setNewAsset(prev => ({ ...prev, incomeRate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        placeholder="e.g. 4"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Growth rate (% p.a.)</label>
                                    <input
                                        type="number"
                                        value={newAsset.growthRate}
                                        onChange={(e) => setNewAsset(prev => ({ ...prev, growthRate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        placeholder="e.g. 5"
                                    />
                                </div>
                                {newAsset.type === 'shares' && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Franking credit (%)</label>
                                        <input
                                            type="number"
                                            value={newAsset.frankingCredit}
                                            onChange={(e) => setNewAsset(prev => ({ ...prev, frankingCredit: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                            placeholder="e.g. 70"
                                        />
                                    </div>
                                )}
                                <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                                    <button
                                        type="button"
                                        onClick={resetAssetForm}
                                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 underline"
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleAddAsset}
                                        className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        Add asset
                                    </button>
                                </div>
                            </div>
                        )}

                        {addedAssets.length > 0 && (
                            <div className="space-y-3">
                                {addedAssets.map(asset => {
                                    const config = SUPPORT_ASSET_OPTIONS.find(opt => opt.id === asset.type);
                                    return (
                                        <div key={asset.id} className="p-3 bg-white rounded-lg border border-blue-200">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-sm">{config?.label || 'Asset'} — {asset.description}</p>
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Ownership: {ownershipOptions.find(option => option.value === asset.ownership)?.label || asset.ownership}
                                                    </p>
                                                    <p className="text-xs text-gray-600">
                                                        Income {asset.incomeRate || '0'}% p.a. | Growth {asset.growthRate || '0'}% p.a.
                                                        {asset.frankingCredit ? ` | Franking ${asset.frankingCredit}%` : ''}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="font-bold text-blue-700">{formatCurrencyLocal(parseFloat(String(asset.value)) || 0)}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAsset(asset.id)}
                                                        className="p-1 rounded-full bg-white text-gray-500 hover:text-red-600 hover:bg-red-50"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-1 text-xs text-gray-500">
                <strong>Any surplus is automatically added to your bank account balance for ongoing cashflow modelling.</strong>
            </div>
        </div>
    );
}

export interface ReverseMortgageAllocatorDetails {
    loanAmount?: number | string;
    loanPercentage?: number | string;
    interestRate?: number | string;
    loanStructure?: 'lump_sum' | 'regular_payments' | string;
    regularPaymentAmount?: number | string;
    regularPaymentFrequency?: 'fortnightly' | 'monthly' | string;
    establishmentFees?: number | string;
    [key: string]: unknown;
}

export interface ReverseMortgageStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: ReverseMortgageAllocatorDetails;
    setDetails: React.Dispatch<React.SetStateAction<ReverseMortgageAllocatorDetails>>;
    legacyYears?: number;
}

export function ReverseMortgageStrategyAllocator({ factFind, details, setDetails, legacyYears: legacyYearsProp }: ReverseMortgageStrategyAllocatorProps) {
    const [isEducationExpanded, setIsEducationExpanded] = useState(false);
    const legacyYears = legacyYearsProp ?? 5;
    const formatCurrency = (value: number | string | undefined) => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value) || 0);
    };

    const handleDetailChange = (field: keyof ReverseMortgageAllocatorDetails, value: string | number | undefined) => {
        if (field === 'loanPercentage') {
            const roundedPercentage = Math.round(parseFloat(String(value)) || 0);
            setDetails(prev => ({ ...prev, [field]: roundedPercentage }));
            const homeValue = parseFloat(String(factFind.home?.value)) || 0;
            const loanAmount = (homeValue * roundedPercentage / 100);
            setDetails(prev => ({ ...prev, loanAmount }));
        } else if (field === 'loanAmount') {
            setDetails(prev => ({ ...prev, [field]: parseFloat(String(value)) || 0 }));
            const homeValue = parseFloat(String(factFind.home?.value)) || 0;
            if (homeValue > 0) {
                const loanPercentage = Math.round(((parseFloat(String(value)) || 0) / homeValue) * 100);
                setDetails(prev => ({ ...prev, loanPercentage }));
            }
        } else if (field === 'interestRate') {
            setDetails(prev => ({ ...prev, [field]: parseFloat(String(value)) || 0 }));
        } else {
            setDetails(prev => ({ ...prev, [field]: value }));
        }
    };

    const homeValue = parseFloat(String(factFind.home?.value)) || 0;
    const clientAge = calculateAge(factFind.client?.dob) || 0;
    const partnerAge = factFind.adviceFor === 'couple' ? calculateAge(factFind.partner?.dob) : null;
    const youngestAge = partnerAge ? Math.min(clientAge, partnerAge) : clientAge;

    const getMaxLVR = (age: number) => {
        if (age >= 80) return 50;
        if (age >= 75) return 45;
        if (age >= 70) return 40;
        if (age >= 65) return 30;
        if (age >= 60) return 20;
        return 15;
    };

    const maxLVR = getMaxLVR(youngestAge);
    const maxLoanAmount = homeValue * (maxLVR / 100);
    const currentLoanAmount = Number(details.loanAmount) || 0;
    const currentLoanPercentage = Number(details.loanPercentage) || 0;
    const exceedsLimit = currentLoanPercentage > maxLVR;

    const homeGrowthRate = parseFloat(String(factFind.home?.homeGrowthRate)) || 5;
    const initialLoanBalance = currentLoanAmount || 0;
    const interestRate = parseFloat(String(details.interestRate)) || REVERSE_MORTGAGE_DEFAULT_RATE;

    let projectedHomeValueWithRM = homeValue;
    let projectedLoanBalance = initialLoanBalance;

    for (let year = 1; year <= legacyYears; year++) {
        projectedHomeValueWithRM = projectedHomeValueWithRM * (1 + homeGrowthRate / 100);
        projectedLoanBalance = projectedLoanBalance * (1 + interestRate / 100);

        if (details.loanStructure === 'regular_payments' && year >= 1) {
            const paymentAmount = parseFloat(String(details.regularPaymentAmount)) || 0;
            const frequency = details.regularPaymentFrequency || 'fortnightly';
            const annualPayment = frequency === 'fortnightly' ? paymentAmount * 26 : paymentAmount * 12;
            projectedLoanBalance += annualPayment;
        }
    }

    let projectedHomeValueNoRM = homeValue;
    for (let year = 1; year <= legacyYears; year++) {
        projectedHomeValueNoRM = projectedHomeValueNoRM * (1 + homeGrowthRate / 100);
    }

    const projectedEquityWithRM = Math.max(0, projectedHomeValueWithRM - projectedLoanBalance);
    const projectedEquityNoRM = projectedHomeValueNoRM;
    const equityDifference = projectedEquityNoRM - projectedEquityWithRM;
    const equityPercentageOwned = initialLoanBalance > 0 ? (projectedEquityWithRM / projectedHomeValueWithRM * 100) : 100;

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
            <h4 className="font-semibold text-lg">Reverse Mortgage Details</h4>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                    onClick={() => setIsEducationExpanded(!isEducationExpanded)}
                    className="w-full p-4 bg-white border border-slate-200 transition-all duration-200 flex items-center justify-between"
                >
                    <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Info className="w-5 h-5 text-blue-600" />
                        What is a Reverse Mortgage?
                    </h5>
                    {isEducationExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-600 transform rotate-180 transition-transform" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600 transition-transform" />
                    )}
                </button>
                {isEducationExpanded && (
                    <div className="p-4 bg-white border-t border-gray-200 space-y-3">
                        <div className="text-sm text-gray-700 space-y-3">
                            <p>
                                A <strong>reverse mortgage</strong> is a loan that allows you to access the equity in your home without making regular repayments. 
                                Instead, interest compounds on the loan balance, and the loan is typically repaid when you sell the home, move into aged care, or pass away.
                            </p>
                            
                            <div>
                                <p className="font-semibold mb-2">Loan Structure Options:</p>
                                <div className="space-y-3 pl-4 border-l-2 border-blue-200">
                                    <div>
                                        <p className="font-medium text-blue-700">1. Lump Sum</p>
                                        <p className="text-xs text-gray-600 mt-1">
                                            You receive the full loan amount upfront. This gives you immediate access to cash for expenses or investments. 
                                            The entire loan amount immediately starts accruing compound interest.
                                        </p>
                                    </div>
                                    <div>
                                        <p className="font-medium text-blue-700">2. Regular Payments</p>
                                        <p className="text-xs text-gray-600 mt-1">
                                            You receive regular payments (fortnightly or monthly) over time. This provides a steady income stream but may be more expensive 
                                            as the loan balance accumulates with each payment.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-white border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Impact on Age Pension:</p>
                                <p className="text-xs text-gray-700 mb-2">
                                    When you receive reverse mortgage proceeds, these funds are added to your assessable assets for the Age Pension means test. 
                                    The loan proceeds are subject to deeming rules, which means Centrelink assumes they earn income at prescribed rates.
                                </p>
                            </div>
                            
                            <div className="mt-3 p-3 bg-white border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Impact on Support at Home Contributions:</p>
                                <p className="text-xs text-gray-700 mb-2">
                                    Reverse mortgage proceeds are treated as assessable assets for Support at Home contribution calculations. 
                                    Higher assets may increase your contribution rate, which could offset some of the benefits of accessing the equity.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            {exceedsLimit && (
                <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded-r-lg text-sm">
                    <p className="text-red-700 font-semibold">âš ï¸ Warning: Your loan amount exceeds typical lender limits for your age ({youngestAge}).</p>
                    <p className="text-red-600 mt-1 text-xs">Most lenders restrict reverse mortgages to a maximum of {maxLVR}% of home value ({formatCurrency(maxLoanAmount)}) based on the youngest borrower's age.</p>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <CurrencyInput
                        label="Loan Amount ($)"
                        value={details.loanAmount ?? ''}
                        onChange={(v) => handleDetailChange('loanAmount', v)}
                        placeholder="0"
                        className=""
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        Loan Percentage (%)
                        <InfoIcon text="This shows what percentage of your home's current value the loan amount represents." />
                    </label>
                    <input
                        type="number"
                        value={Math.round(Number(details.loanPercentage) || 0)}
                        onChange={(e) => handleDetailChange('loanPercentage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                        max={maxLVR}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Interest Rate (% p.a.)</label>
                    <input
                        type="number"
                        step="0.1"
                        value={details.interestRate || REVERSE_MORTGAGE_DEFAULT_RATE}
                        onChange={(e) => handleDetailChange('interestRate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Loan Structure</label>
                    <select
                        value={details.loanStructure || 'lump_sum'}
                        onChange={(e) => handleDetailChange('loanStructure', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="lump_sum">Lump Sum</option>
                        <option value="regular_payments">Regular Payments</option>
                    </select>
                </div>
            </div>

            {details.loanStructure === 'regular_payments' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <CurrencyInput
                            label="Payment Amount ($)"
                            value={details.regularPaymentAmount ?? ''}
                            onChange={(v) => handleDetailChange('regularPaymentAmount', v)}
                            placeholder="0"
                            className=""
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Frequency</label>
                        <select
                            value={details.regularPaymentFrequency || 'fortnightly'}
                            onChange={(e) => handleDetailChange('regularPaymentFrequency', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="fortnightly">Fortnightly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                </div>
            )}

            <div>
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    Establishment Fees ($)
                    <InfoIcon text="One-time fees charged by the lender to set up the reverse mortgage." />
                </label>
                <CurrencyInput
                    value={details.establishmentFees ?? ''}
                    onChange={(v) => handleDetailChange('establishmentFees', v)}
                    placeholder="0"
                    className=""
                />
            </div>
            </div>

            {initialLoanBalance > 0 && (
                <div className="space-y-4">
                    <h5 className="font-semibold text-gray-900 text-lg">Impact on Home Equity</h5>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-white border-2 border-blue-200 rounded-lg">
                            <h6 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                <TrendingDown className="w-4 h-4" />
                                With Reverse Mortgage
                            </h6>
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700">Home Equity Ownership</span>
                                    <span className="text-lg font-bold text-blue-700">{equityPercentageOwned.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                    <div 
                                        className="bg-blue-600 h-6 flex items-center justify-end pr-2 transition-all duration-500"
                                        style={{ width: `${Math.min(100, equityPercentageOwned)}%` }}
                                    >
                                        {equityPercentageOwned > 15 && (
                                            <span className="text-xs font-semibold text-white">{equityPercentageOwned.toFixed(1)}%</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Projected Home Value:</span>
                                    <span className="font-medium">{formatCurrency(projectedHomeValueWithRM)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Loan Balance:</span>
                                    <span className="font-medium text-red-600">-{formatCurrency(projectedLoanBalance)}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-blue-200">
                                    <span className="font-semibold text-gray-900">Your Equity:</span>
                                    <span className="font-bold text-blue-700">{formatCurrency(projectedEquityWithRM)}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-4 bg-white border-2 border-green-200 rounded-lg">
                            <h6 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Without Reverse Mortgage
                            </h6>
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700">Home Equity Ownership</span>
                                    <span className="text-lg font-bold text-green-700">100%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                    <div className="bg-emerald-600 h-6 flex items-center justify-end pr-2" style={{ width: '100%' }}>
                                        <span className="text-xs font-semibold text-white">100%</span>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Projected Home Value:</span>
                                    <span className="font-medium">{formatCurrency(projectedHomeValueNoRM)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Loan Balance:</span>
                                    <span className="font-medium text-gray-400">$0</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-green-200">
                                    <span className="font-semibold text-gray-900">Your Equity:</span>
                                    <span className="font-bold text-green-700">{formatCurrency(projectedEquityNoRM)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-gray-900">Equity Impact After {legacyYears} Years:</span>
                            <span className="text-lg font-bold text-red-600">-{formatCurrency(equityDifference)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            By taking out a reverse mortgage, your home equity will be <strong>{formatCurrency(equityDifference)} less</strong> after {legacyYears} years compared to not taking the loan.
                        </p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">With Reverse Mortgage:</span>
                                <span className="font-semibold text-blue-700 ml-2">{formatCurrency(projectedEquityWithRM)}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Without Reverse Mortgage:</span>
                                <span className="font-semibold text-green-700 ml-2">{formatCurrency(projectedEquityNoRM)}</span>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                            Note: This projection assumes home growth at {homeGrowthRate}% p.a. and loan interest at {interestRate}% p.a. 
                            Actual results may vary. Use the "View Projections" button for a detailed analysis including all assets.
                        </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 border-l-4 border-gray-400 rounded-r-lg text-sm">
                        <p className="font-semibold text-gray-900 mb-2">Additional Considerations:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                            <li>Compound interest will erode your home equity over time</li>
                            <li>Loan balance grows faster as you age</li>
                            <li>Limited flexibility to move or refinance</li>
                            <li>Loan proceeds may be assessable for Age Pension and Support at Home contribution calculations</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

export interface HeasAllocatorDetails {
    paymentAmount?: number | string;
    paymentFrequency?: 'fortnightly' | 'monthly' | string;
    interestRate?: number | string;
    [key: string]: unknown;
}

export interface HEASStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: HeasAllocatorDetails;
    setDetails: React.Dispatch<React.SetStateAction<HeasAllocatorDetails>>;
    legacyYears?: number;
}

export function HEASStrategyAllocator({ factFind, details, setDetails, legacyYears: legacyYearsProp }: HEASStrategyAllocatorProps) {
    const [isEducationExpanded, setIsEducationExpanded] = useState(false);
    const legacyYears = legacyYearsProp ?? 5;
    const formatCurrency = (value: number | string | undefined) => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value) || 0);
    };

    const handleDetailChange = (field: keyof HeasAllocatorDetails, value: string | number | undefined) => {
        setDetails(prev => ({ ...prev, [field]: value }));
    };

    const [isOnAgePension, setIsOnAgePension] = useState(false);
    useEffect(() => {
        let cancelled = false;
        calculationApi.calculateFinancials(factFind).then((financials) => {
            if (!cancelled) setIsOnAgePension((Number(financials?.socialSecurity) || 0) > 0);
        });
        return () => { cancelled = true; };
    }, [factFind]);
    const relationshipStatus = factFind.adviceFor === 'couple' ? 'couple' : 'single';
    const maxPensionRate = PENSION_RATES[relationshipStatus] || PENSION_RATES.single;
    const maxHEASPayment = maxPensionRate * 1.5;
    const maxHEASPaymentFortnightly = maxPensionRate / 26 * 1.5;

    const annualPaymentAmount = details.paymentFrequency === 'fortnightly'
        ? (Number(details.paymentAmount) || 0) * 26
        : (Number(details.paymentAmount) || 0) * 12;

    const homeValue = parseFloat(String(factFind.home?.value)) || 0;
    const homeGrowthRate = parseFloat(String(factFind.home?.homeGrowthRate)) || 5;
    const interestRate = parseFloat(String(details.interestRate)) || 3.95;

    let projectedHomeValueWithHEAS = homeValue;
    let projectedHEASLoanBalance = 0;

    for (let year = 1; year <= legacyYears; year++) {
        projectedHomeValueWithHEAS = projectedHomeValueWithHEAS * (1 + homeGrowthRate / 100);
        projectedHEASLoanBalance = projectedHEASLoanBalance * (1 + interestRate / 100);
        projectedHEASLoanBalance += annualPaymentAmount;
    }

    let projectedHomeValueNoHEAS = homeValue;
    for (let year = 1; year <= legacyYears; year++) {
        projectedHomeValueNoHEAS = projectedHomeValueNoHEAS * (1 + homeGrowthRate / 100);
    }

    const projectedEquityWithHEAS = Math.max(0, projectedHomeValueWithHEAS - projectedHEASLoanBalance);
    const projectedEquityNoHEAS = projectedHomeValueNoHEAS;
    const equityDifference = projectedEquityNoHEAS - projectedEquityWithHEAS;
    const equityPercentageOwned = annualPaymentAmount > 0 ? (projectedEquityWithHEAS / projectedHomeValueWithHEAS * 100) : 100;

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
            <h4 className="font-semibold text-lg">Home Equity Access Scheme (HEAS) Details</h4>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                    onClick={() => setIsEducationExpanded(!isEducationExpanded)}
                    className="w-full p-4 bg-white border border-slate-200 transition-all duration-200 flex items-center justify-between"
                >
                    <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Info className="w-5 h-5 text-blue-600" />
                        What is HEAS?
                    </h5>
                    {isEducationExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-600 transform rotate-180 transition-transform" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600 transition-transform" />
                    )}
                </button>
                {isEducationExpanded && (
                    <div className="p-4 bg-white border-t border-gray-200 space-y-3">
                        <div className="text-sm text-gray-700 space-y-3">
                            <p>
                                The <strong>Home Equity Access Scheme (HEAS)</strong> is a government program that allows Age Pension recipients to access equity in their home 
                                through a loan scheme. Unlike commercial reverse mortgages, HEAS is administered by Services Australia (Centrelink) and offers a lower interest rate 
                                (currently {details.interestRate}% p.a.) and specific eligibility requirements.
                            </p>
                            
                            <div className="p-3 bg-white border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Key Differences from Commercial Reverse Mortgages:</p>
                                <ul className="text-xs text-gray-700 space-y-2 list-disc list-inside">
                                    <li><strong>Lower interest rate:</strong> HEAS charges {details.interestRate}% p.a. (government-set), compared to typically 6-8%+ for commercial reverse mortgages.</li>
                                    <li><strong>Age Pension requirement:</strong> You must be receiving or eligible for Age Pension to access HEAS.</li>
                                    <li><strong>Dollar-for-dollar reduction:</strong> HEAS payments reduce your Age Pension dollar-for-dollar.</li>
                                    <li><strong>Payment flexibility:</strong> You can start and stop HEAS payments as needed.</li>
                                    <li><strong>Maximum payment:</strong> HEAS is limited to 150% of the maximum Age Pension rate (currently {formatCurrency(maxHEASPayment)} per year).</li>
                                </ul>
                            </div>
                            
                            <div className="mt-4 p-3 bg-white border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Impact on Age Pension:</p>
                                <p className="text-xs text-gray-700 mb-2">
                                    HEAS payments reduce your Age Pension dollar-for-dollar{annualPaymentAmount > 0 ? `. This means if you receive ${formatCurrency(annualPaymentAmount)} per year from HEAS, your Age Pension will be reduced by ${formatCurrency(annualPaymentAmount)} per year.` : ''} While this may seem counterintuitive, HEAS can still be beneficial because:
                                </p>
                                <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
                                    <li>You receive the payment immediately</li>
                                    <li>The loan balance grows at a lower rate ({details.interestRate}% p.a.) than commercial options</li>
                                    <li>You maintain control over when and how much you draw</li>
                                </ul>
                            </div>
                            
                            <div className="mt-3 p-3 bg-white border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Impact on Support at Home Contributions:</p>
                                <p className="text-xs text-gray-700 mb-2">
                                    HEAS payments are treated as assessable income for Support at Home contribution calculations. This may increase your contribution rate, 
                                    which could offset some of the benefits of accessing the equity.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            {!isOnAgePension && (
                <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded-r-lg text-sm">
                    <p className="text-red-800 font-semibold">âš ï¸ Eligibility Requirement: You must be receiving Age Pension or be eligible for Age Pension to access HEAS.</p>
                </div>
            )}

            <div className="p-3 bg-white border-l-4 border-blue-400 rounded-r-lg text-sm">
                <p><strong className="text-blue-700">Maximum Available:</strong> {formatCurrency(maxHEASPaymentFortnightly)} per fortnight ({formatCurrency(maxHEASPayment)} per year) - 150% of maximum Age Pension rate</p>
                <p className="mt-2"><strong className="text-blue-700">Interest Rate:</strong> {details.interestRate}% p.a. (compound) - Government-set rate</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <CurrencyInput
                        label="Payment Amount ($)"
                        value={details.paymentAmount ?? ''}
                        onChange={(v) => handleDetailChange('paymentAmount', v)}
                        placeholder="0"
                        className=""
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Max: {formatCurrency(details.paymentFrequency === 'fortnightly' ? maxHEASPaymentFortnightly : maxHEASPayment / 12)} per {details.paymentFrequency === 'fortnightly' ? 'fortnight' : 'month'}
                    </p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Frequency</label>
                    <select
                        value={details.paymentFrequency || 'fortnightly'}
                        onChange={(e) => handleDetailChange('paymentFrequency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option>
                    </select>
                </div>
            </div>

            <div className="p-3 bg-white border-l-4 border-green-400 rounded-r-lg text-sm">
                <p><strong className="text-green-700">Annual Payment:</strong> {formatCurrency(annualPaymentAmount)}</p>
                {isOnAgePension && (
                    <p className="mt-2"><strong className="text-green-700">Age Pension Impact:</strong> Your Age Pension will be reduced by {formatCurrency(annualPaymentAmount)} per year (dollar-for-dollar reduction)</p>
                )}
            </div>

            {annualPaymentAmount > 0 && (
                <div className="space-y-4">
                    <h5 className="font-semibold text-gray-900 text-lg">Impact on Home Equity</h5>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-white border-2 border-blue-200 rounded-lg">
                            <h6 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                <TrendingDown className="w-4 h-4" />
                                With HEAS
                            </h6>
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700">Home Equity Ownership</span>
                                    <span className="text-lg font-bold text-blue-700">{equityPercentageOwned.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                    <div 
                                        className="bg-blue-600 h-6 flex items-center justify-end pr-2 transition-all duration-500"
                                        style={{ width: `${Math.min(100, equityPercentageOwned)}%` }}
                                    >
                                        {equityPercentageOwned > 15 && (
                                            <span className="text-xs font-semibold text-white">{equityPercentageOwned.toFixed(1)}%</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Projected Home Value:</span>
                                    <span className="font-medium">{formatCurrency(projectedHomeValueWithHEAS)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">HEAS Loan Balance:</span>
                                    <span className="font-medium text-red-600">-{formatCurrency(projectedHEASLoanBalance)}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-blue-200">
                                    <span className="font-semibold text-gray-900">Your Equity:</span>
                                    <span className="font-bold text-blue-700">{formatCurrency(projectedEquityWithHEAS)}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-4 bg-white border-2 border-green-200 rounded-lg">
                            <h6 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Without HEAS
                            </h6>
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700">Home Equity Ownership</span>
                                    <span className="text-lg font-bold text-green-700">100%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                    <div className="bg-emerald-600 h-6 flex items-center justify-end pr-2" style={{ width: '100%' }}>
                                        <span className="text-xs font-semibold text-white">100%</span>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Projected Home Value:</span>
                                    <span className="font-medium">{formatCurrency(projectedHomeValueNoHEAS)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">HEAS Loan Balance:</span>
                                    <span className="font-medium text-gray-400">$0</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-green-200">
                                    <span className="font-semibold text-gray-900">Your Equity:</span>
                                    <span className="font-bold text-green-700">{formatCurrency(projectedEquityNoHEAS)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-gray-900">Equity Impact After {legacyYears} Years:</span>
                            <span className="text-lg font-bold text-red-600">-{formatCurrency(equityDifference)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            By taking out HEAS, your home equity will be <strong>{formatCurrency(equityDifference)} less</strong> after {legacyYears} years compared to not taking the loan.
                        </p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">With HEAS:</span>
                                <span className="font-semibold text-blue-700 ml-2">{formatCurrency(projectedEquityWithHEAS)}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Without HEAS:</span>
                                <span className="font-semibold text-green-700 ml-2">{formatCurrency(projectedEquityNoHEAS)}</span>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                            Note: This projection assumes home growth at {homeGrowthRate}% p.a. and loan interest at {interestRate}% p.a. 
                            Actual results may vary. Use the "View Projections" button for a detailed analysis including all assets.
                        </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 border-l-4 border-gray-400 rounded-r-lg text-sm">
                        <p className="font-semibold text-gray-900 mb-2">Additional Considerations:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                            <li>HEAS payments reduce your Age Pension dollar-for-dollar</li>
                            <li>Compound interest will erode your home equity over time</li>
                            <li>Loan balance grows at a lower rate than commercial reverse mortgages</li>
                            <li>HEAS payments may be assessable for Support at Home contribution calculations</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

/** A liquid/investment asset available to fund a renovate or upgrade strategy, surfaced from factFind.assets. */
interface PropertyFundingCandidateAsset {
    id: string | number | undefined;
    type: string;
    label: string;
    value: number;
    ownership: string;
    costBase?: number;
}

export interface RenovateStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: RenovateDetails;
    setDetails: React.Dispatch<React.SetStateAction<RenovateDetails>>;
    beforeFinancials?: FinancialsForCgt;
}

export function RenovateStrategyAllocator({ factFind, details, setDetails, beforeFinancials }: RenovateStrategyAllocatorProps) {
    const formatCurrency = (value: number | string | undefined) => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value) || 0);
    };

    const handleDetailChange = (field: keyof RenovateDetails, value: number | string | undefined | PropertyStrategyFundingSource[]) => {
        setDetails(prev => ({ ...prev, [field]: value }));
    };

    const renovationAmount = parseFloat(String(details.renovationAmount)) || 0;
    const currentHomeValue = parseFloat(String(factFind.home?.value)) || 0;
    const fundingSources = details.fundingSources || [];

    // Calculate available assets for funding (including ABPs)
    const availableAssets: PropertyFundingCandidateAsset[] = [];
    (factFind.assets?.bankAccounts || []).forEach(account => {
        const value = parseFloat(String(account.value)) || 0;
        if (value > 0) {
            availableAssets.push({
                id: account.id,
                type: 'bankAccounts',
                label: assetDisplayName(account, factFind, 'Bank account'),
                value: value,
                ownership: account.ownership || 'client'
            });
        }
    });
    (factFind.assets?.accountBasedPensions || []).forEach(abp => {
        const value = parseFloat(String(abp.value)) || 0;
        if (value > 0) {
            availableAssets.push({
                id: abp.id,
                type: 'accountBasedPensions',
                label: assetDisplayName(abp, factFind, 'Account-based pension'),
                value: value,
                ownership: abp.ownership || 'client'
            });
        }
    });
    (factFind.assets?.shares || []).forEach(share => {
        const value = parseFloat(String(share.value)) || 0;
        if (value > 0) {
            availableAssets.push({
                id: share.id,
                type: 'shares',
                label: assetDisplayName(share, factFind, 'Shares / managed fund'),
                value: value,
                ownership: share.ownership || 'client',
                costBase: parseFloat(String(share.costBase)) || 0
            });
        }
    });
    (factFind.assets?.investmentProperty || []).forEach(property => {
        const value = parseFloat(String(property.value)) || 0;
        if (value > 0) {
            availableAssets.push({
                id: property.id,
                type: 'investmentProperty',
                label: assetDisplayName(property, factFind, 'Investment property'),
                value: value,
                ownership: property.ownership || 'client',
                costBase: parseFloat(String(property.costBase)) || 0
            });
        }
    });

    // Get assets already selected for funding
    const selectedAssetIds = fundingSources.map(f => `${f.assetType}-${f.assetId}`);
    const availableAssetsForSelection = availableAssets.filter(asset =>
        !selectedAssetIds.includes(`${asset.type}-${asset.id}`)
    );

    // Calculate total allocated and CGT
    let totalAllocated = 0;
    let cgtTotal = 0;
    fundingSources.forEach(funding => {
        const amount = parseFloat(String(funding.amount)) || 0;
        const asset = availableAssets.find(a => String(a.id) === String(funding.assetId) && a.type === funding.assetType);
        // Only what the account still holds counts (25 Oct 2026): an account a linked aged care plan has
        // already spent raises nothing, and the calculation takes the rest from the bank.
        totalAllocated += asset ? Math.min(amount, asset.value) : 0;

        if (asset && asset.costBase && ['shares', 'investmentProperty'].includes(asset.type)) {
            const saleAmount = Math.min(amount, asset.value);
            cgtTotal += calculateCgtForStrategy(asset as unknown as Parameters<typeof calculateCgtForStrategy>[0], saleAmount, beforeFinancials);
        }
    });

    const netCost = renovationAmount + cgtTotal;
    const newHomeValue = currentHomeValue + renovationAmount;
    const assessableAssetReduction = renovationAmount;
    const remainingNeeded = Math.max(0, renovationAmount - totalAllocated);

    const addFundingSource = () => {
        if (availableAssetsForSelection.length === 0) return;
        const newSource: PropertyStrategyFundingSource = {
            assetId: availableAssetsForSelection[0].id as string | number,
            assetType: availableAssetsForSelection[0].type,
            amount: 0
        };
        handleDetailChange('fundingSources', [...fundingSources, newSource]);
    };

    const removeFundingSource = (index: number) => {
        const updated = fundingSources.filter((_, i) => i !== index);
        handleDetailChange('fundingSources', updated);
    };

    const updateFundingSource = (index: number, field: keyof PropertyStrategyFundingSource, value: number | string) => {
        const updated = [...fundingSources];
        updated[index] = { ...updated[index], [field]: value };
        handleDetailChange('fundingSources', updated);
    };

    return (
        <div className="mt-6 space-y-4 border-t border-gray-200 pt-6">
            <div className="bg-white border-l-4 border-blue-500 rounded-r-lg p-4">
                <p className="text-sm text-blue-900">
                    <strong>How this works:</strong> Spending money on home renovations converts assessable assets (cash, shares, ABPs, etc.) into exempt home equity.
                    The home is fully exempt from the Age Pension asset test, so this strategy can help you qualify for or increase Age Pension.
                </p>
            </div>

            <div className="space-y-4">
                <div>
                    <CurrencyInput
                        label="Renovation Amount"
                        value={details.renovationAmount ?? ''}
                        onChange={(v) => handleDetailChange('renovationAmount', v)}
                        placeholder="0"
                        className=""
                    />
                </div>
                
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Source of Funds
                        </label>
                        {availableAssetsForSelection.length > 0 && (
                            <button
                                type="button"
                                onClick={addFundingSource}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                                <Plus className="w-3 h-3" />
                                Add asset
                            </button>
                        )}
                    </div>
                    
                    {fundingSources.length === 0 && (
                        <select
                            onChange={(e) => {
                                if (e.target.value) {
                                    const [assetType, assetId] = e.target.value.split('-');
                                    const selected = availableAssets.find(a => 
                                        a.id === assetId && a.type === assetType
                                    );
                                    if (selected) {
                                        handleDetailChange('fundingSources', [{
                                            assetId: selected.id as string | number,
                                            assetType: selected.type,
                                            amount: 0
                                        }]);
                                    }
                                }
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Select an asset to fund from...</option>
                            {availableAssets.map(asset => (
                                <option key={`${asset.type}-${asset.id}`} value={`${asset.type}-${asset.id}`}>
                                    {asset.label} – {formatCurrency(asset.value)}
                                </option>
                            ))}
                        </select>
                    )}
                    
                    {fundingSources.map((funding, index) => {
                        const asset = availableAssets.find(a => String(a.id) === String(funding.assetId) && a.type === funding.assetType);
                        const availableForThisAsset = asset ? parseFloat(String(asset.value)) || 0 : 0;
                        const allocatedToThisAsset = parseFloat(String(funding.amount)) || 0;
                        const remainingForThisAsset = availableForThisAsset - allocatedToThisAsset;
                        
                        return (
                            <div key={index} className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">
                                        {asset ? asset.label : 'An account no longer held — this part is taken from the bank'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeFundingSource(index)}
                                        className="text-red-600 hover:text-red-800"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="text-xs text-gray-500 mb-2">
                                    Available: {formatCurrency(availableForThisAsset)}
                                </div>
                                <CurrencyInput
                                    value={funding.amount ?? ''}
                                    onChange={(v) => updateFundingSource(index, 'amount', v ?? 0)}
                                    placeholder="0"
                                    className=""
                                />
                                {remainingForThisAsset > 0 && remainingNeeded > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => updateFundingSource(index, 'amount', Math.min(remainingForThisAsset, remainingNeeded + allocatedToThisAsset))}
                                        className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                                    >
                                        Use remaining needed ({formatCurrency(Math.min(remainingForThisAsset, remainingNeeded))})
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    
                    {fundingSources.length > 0 && availableAssetsForSelection.length > 0 && (
                        <button
                            type="button"
                            onClick={addFundingSource}
                            className="w-full mt-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Add another asset
                        </button>
                    )}
                    
                    {remainingNeeded > 0 && (
                        <div className="mt-2 text-sm text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded p-2">
                            Remaining needed: {formatCurrency(remainingNeeded)}
                        </div>
                    )}
                    {totalAllocated > renovationAmount && (
                        <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                            Total allocated ({formatCurrency(totalAllocated)}) exceeds renovation amount ({formatCurrency(renovationAmount)})
                        </div>
                    )}
                </div>
                
                {renovationAmount > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Renovation Cost:</span>
                            <span className="font-semibold">{formatCurrency(renovationAmount)}</span>
                        </div>
                        {cgtTotal > 0 && (
                            <div className="flex justify-between text-red-600">
                                <span className="text-sm">Capital Gains Tax:</span>
                                <span className="font-semibold">-{formatCurrency(cgtTotal)}</span>
                            </div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-gray-300">
                            <span className="text-sm font-semibold text-gray-900">Total Cost:</span>
                            <span className="font-bold text-gray-900">{formatCurrency(netCost)}</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-300">
                            <div className="flex justify-between mb-1">
                                <span className="text-sm text-gray-600">New Home Value:</span>
                                <span className="font-semibold text-green-600">{formatCurrency(newHomeValue)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Assessable Asset Reduction:</span>
                                <span className="font-semibold text-blue-600">{formatCurrency(assessableAssetReduction)}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export interface UpgradeStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: UpgradeDetails;
    setDetails: React.Dispatch<React.SetStateAction<UpgradeDetails>>;
    beforeFinancials?: FinancialsForCgt;
}

export function UpgradeStrategyAllocator({ factFind, details, setDetails, beforeFinancials }: UpgradeStrategyAllocatorProps) {
    const formatCurrency = (value: number | string | undefined) => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value) || 0);
    };

    const handleDetailChange = (field: keyof UpgradeDetails, value: number | string | undefined | PropertyStrategyFundingSource[]) => {
        setDetails(prev => ({ ...prev, [field]: value }));
    };

    const currentHomeValue = parseFloat(String(details.currentHomeValue || factFind.home?.value)) || 0;
    const newHomePurchasePrice = parseFloat(String(details.newHomePurchasePrice)) || 0;
    const sellingCosts = parseFloat(String(details.sellingCosts)) || 0;
    const fundingSources = details.fundingSources || [];

    const netProceedsFromSale = Math.max(0, currentHomeValue - sellingCosts);
    const additionalFundsNeeded = Math.max(0, newHomePurchasePrice - netProceedsFromSale);

    // Calculate available assets for funding (including ABPs)
    const availableAssets: PropertyFundingCandidateAsset[] = [];
    (factFind.assets?.bankAccounts || []).forEach(account => {
        const value = parseFloat(String(account.value)) || 0;
        if (value > 0) {
            availableAssets.push({
                id: account.id,
                type: 'bankAccounts',
                label: assetDisplayName(account, factFind, 'Bank account'),
                value: value,
                ownership: account.ownership || 'client'
            });
        }
    });
    (factFind.assets?.accountBasedPensions || []).forEach(abp => {
        const value = parseFloat(String(abp.value)) || 0;
        if (value > 0) {
            availableAssets.push({
                id: abp.id,
                type: 'accountBasedPensions',
                label: assetDisplayName(abp, factFind, 'Account-based pension'),
                value: value,
                ownership: abp.ownership || 'client'
            });
        }
    });
    (factFind.assets?.shares || []).forEach(share => {
        const value = parseFloat(String(share.value)) || 0;
        if (value > 0) {
            availableAssets.push({
                id: share.id,
                type: 'shares',
                label: assetDisplayName(share, factFind, 'Shares / managed fund'),
                value: value,
                ownership: share.ownership || 'client',
                costBase: parseFloat(String(share.costBase)) || 0
            });
        }
    });
    (factFind.assets?.investmentProperty || []).forEach(property => {
        const value = parseFloat(String(property.value)) || 0;
        if (value > 0) {
            availableAssets.push({
                id: property.id,
                type: 'investmentProperty',
                label: assetDisplayName(property, factFind, 'Investment property'),
                value: value,
                ownership: property.ownership || 'client',
                costBase: parseFloat(String(property.costBase)) || 0
            });
        }
    });

    // Get assets already selected for funding
    const selectedAssetIds = fundingSources.map(f => `${f.assetType}-${f.assetId}`);
    const availableAssetsForSelection = availableAssets.filter(asset =>
        !selectedAssetIds.includes(`${asset.type}-${asset.id}`)
    );

    // Calculate total allocated and CGT
    let totalAllocated = 0;
    let cgtTotal = 0;
    fundingSources.forEach(funding => {
        const amount = parseFloat(String(funding.amount)) || 0;
        const asset = availableAssets.find(a => String(a.id) === String(funding.assetId) && a.type === funding.assetType);
        // Only what the account still holds counts (25 Oct 2026): an account a linked aged care plan has
        // already spent raises nothing, and the calculation takes the rest from the bank.
        totalAllocated += asset ? Math.min(amount, asset.value) : 0;

        if (asset && asset.costBase && ['shares', 'investmentProperty'].includes(asset.type)) {
            const saleAmount = Math.min(amount, asset.value);
            cgtTotal += calculateCgtForStrategy(asset as unknown as Parameters<typeof calculateCgtForStrategy>[0], saleAmount, beforeFinancials);
        }
    });

    const totalCost = additionalFundsNeeded + cgtTotal;
    const assessableAssetReduction = additionalFundsNeeded;
    const remainingNeeded = Math.max(0, additionalFundsNeeded - totalAllocated);

    const addFundingSource = () => {
        if (availableAssetsForSelection.length === 0) return;
        const newSource: PropertyStrategyFundingSource = {
            assetId: availableAssetsForSelection[0].id as string | number,
            assetType: availableAssetsForSelection[0].type,
            amount: 0
        };
        handleDetailChange('fundingSources', [...fundingSources, newSource]);
    };

    const removeFundingSource = (index: number) => {
        const updated = fundingSources.filter((_, i) => i !== index);
        handleDetailChange('fundingSources', updated);
    };

    const updateFundingSource = (index: number, field: keyof PropertyStrategyFundingSource, value: number | string) => {
        const updated = [...fundingSources];
        updated[index] = { ...updated[index], [field]: value };
        handleDetailChange('fundingSources', updated);
    };
    
    return (
        <div className="mt-6 space-y-4 border-t border-gray-200 pt-6">
            <div className="bg-white border-l-4 border-blue-500 rounded-r-lg p-4">
                <p className="text-sm text-blue-900">
                    <strong>How this works:</strong> Buying a more expensive home converts assessable assets (cash, shares, ABPs, etc.) into exempt home equity. 
                    The home is fully exempt from the Age Pension asset test, so this strategy can help you qualify for or increase Age Pension.
                </p>
            </div>
            
            <div className="space-y-4">
                <div>
                    <CurrencyInput
                        label="Current Home Value"
                        value={details.currentHomeValue ?? currentHomeValue ?? ''}
                        onChange={(v) => handleDetailChange('currentHomeValue', v)}
                        placeholder="0"
                        className=""
                    />
                </div>
                
                <div>
                    <CurrencyInput
                        label="New Home Purchase Price"
                        value={details.newHomePurchasePrice ?? ''}
                        onChange={(v) => handleDetailChange('newHomePurchasePrice', v)}
                        placeholder="0"
                        className=""
                    />
                </div>
                
                <div>
                    <CurrencyInput
                        label="Selling Costs (e.g., agent fees, legal)"
                        value={details.sellingCosts ?? ''}
                        onChange={(v) => handleDetailChange('sellingCosts', v)}
                        placeholder="0"
                        className=""
                    />
                </div>
                
                {newHomePurchasePrice > currentHomeValue && (
                    <>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Source of Additional Funds
                                </label>
                                {availableAssetsForSelection.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={addFundingSource}
                                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                    >
                                        <Plus className="w-3 h-3" />
                                        Add asset
                                    </button>
                                )}
                            </div>
                            
                            {fundingSources.length === 0 && (
                                <select
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            const [assetType, assetId] = e.target.value.split('-');
                                            const selected = availableAssets.find(a => 
                                                a.id === assetId && a.type === assetType
                                            );
                                            if (selected) {
                                                handleDetailChange('fundingSources', [{
                                                    assetId: selected.id as string | number,
                                                    assetType: selected.type,
                                                    amount: 0
                                                }]);
                                            }
                                        }
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">Select an asset to fund from...</option>
                                    {availableAssets.map(asset => (
                                        <option key={`${asset.type}-${asset.id}`} value={`${asset.type}-${asset.id}`}>
                                            {asset.label} – {formatCurrency(asset.value)}
                                        </option>
                                    ))}
                                </select>
                            )}
                            
                            {fundingSources.map((funding, index) => {
                                const asset = availableAssets.find(a => String(a.id) === String(funding.assetId) && a.type === funding.assetType);
                                const availableForThisAsset = asset ? parseFloat(String(asset.value)) || 0 : 0;
                                const allocatedToThisAsset = parseFloat(String(funding.amount)) || 0;
                                const remainingForThisAsset = availableForThisAsset - allocatedToThisAsset;
                                
                                return (
                                    <div key={index} className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-700">
                                                {asset ? asset.label : 'An account no longer held — this part is taken from the bank'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeFundingSource(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="text-xs text-gray-500 mb-2">
                                            Available: {formatCurrency(availableForThisAsset)}
                                        </div>
                                        <CurrencyInput
                                            value={funding.amount ?? ''}
                                            onChange={(v) => updateFundingSource(index, 'amount', v ?? 0)}
                                            placeholder="0"
                                            className=""
                                        />
                                        {remainingForThisAsset > 0 && remainingNeeded > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => updateFundingSource(index, 'amount', Math.min(remainingForThisAsset, remainingNeeded + allocatedToThisAsset))}
                                                className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                                            >
                                                Use remaining needed ({formatCurrency(Math.min(remainingForThisAsset, remainingNeeded))})
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            
                            {fundingSources.length > 0 && availableAssetsForSelection.length > 0 && (
                                <button
                                    type="button"
                                    onClick={addFundingSource}
                                    className="w-full mt-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add another asset
                                </button>
                            )}
                            
                            {remainingNeeded > 0 && (
                                <div className="mt-2 text-sm text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded p-2">
                                    Remaining needed: {formatCurrency(remainingNeeded)}
                                </div>
                            )}
                            {totalAllocated > additionalFundsNeeded && (
                                <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                                    Total allocated ({formatCurrency(totalAllocated)}) exceeds additional funds needed ({formatCurrency(additionalFundsNeeded)})
                                </div>
                            )}
                        </div>
                        
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Net Proceeds from Sale:</span>
                                <span className="font-semibold">{formatCurrency(netProceedsFromSale)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Additional Funds Needed:</span>
                                <span className="font-semibold">{formatCurrency(additionalFundsNeeded)}</span>
                            </div>
                            {cgtTotal > 0 && (
                                <div className="flex justify-between text-red-600">
                                    <span className="text-sm">Capital Gains Tax:</span>
                                    <span className="font-semibold">-{formatCurrency(cgtTotal)}</span>
                                </div>
                            )}
                            <div className="flex justify-between pt-2 border-t border-gray-300">
                                <span className="text-sm font-semibold text-gray-900">Total Additional Cost:</span>
                                <span className="font-bold text-gray-900">{formatCurrency(totalCost)}</span>
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-300">
                                <div className="flex justify-between mb-1">
                                    <span className="text-sm text-gray-600">New Home Value:</span>
                                    <span className="font-semibold text-green-600">{formatCurrency(newHomePurchasePrice)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Assessable Asset Reduction:</span>
                                    <span className="font-semibold text-blue-600">{formatCurrency(assessableAssetReduction)}</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                
                {newHomePurchasePrice <= currentHomeValue && newHomePurchasePrice > 0 && (
                    <div className="bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-4">
                        <p className="text-sm text-slate-700">
                            <strong>Note:</strong> The new home price must be higher than your current home value for this to be an upgrade strategy.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
